import { Marker } from 'react-map-gl/maplibre';
import { Truck, Cross, Shield, Plane } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { pointAlongLine } from '@/lib/geo';
import { VEHICLE_SPECS } from '@/data/vehicles';
import type { Vehicle, VehicleStatus } from '@/state/types';

const STATUS_RING: Record<VehicleStatus, string> = {
  1: '#3b82f6', // blue — return
  2: '#22c55e', // green — at base
  3: '#3b82f6', // blue — to scene
  4: '#dc2626', // red — at scene
  5: '#a855f7',
  6: '#64748b',
  7: '#f59e0b', // amber — transport
  8: '#a855f7', // purple — handover
};

/**
 * Renders all vehicles. Position is computed live from routeCoords + progress
 * for transit statuses (1/3/7); for stationary statuses we use vehicle.position.
 *
 * No own state — pulls gameTime from the store so re-renders happen on every tick.
 */
/** Show Status 5 (Sprechwunsch) transiently for this many gameTime ms after a radio message. */
const STATUS_5_FLIP_MS = 3_000;

export function VehicleMarkers() {
  const vehicles = useGameStore((s) => s.vehicles);
  const missions = useGameStore((s) => s.missions);
  const gameTime = useGameStore((s) => s.gameTime);
  const selectDetail = useGameStore((s) => s.selectDetail);

  // Phase 7.2.2 — vehicle ids whose latest radio message is recent enough to
  // flip them to Status 5 in the marker.
  const recentRadioVehicleIds = (() => {
    const set = new Set<string>();
    for (const m of missions) {
      if (!m.radioMessages || m.radioMessages.length === 0) continue;
      const latest = m.radioMessages[m.radioMessages.length - 1];
      if (gameTime - latest.timestamp < STATUS_5_FLIP_MS) {
        set.add(latest.vehicleId);
      }
    }
    return set;
  })();

  return (
    <>
      {vehicles.map((v) => {
        if (v.status === 2) return null;

        const pos = computeLivePosition(v, gameTime);
        const displayStatus = recentRadioVehicleIds.has(v.id) ? 5 : v.status;
        const ring = STATUS_RING[displayStatus] ?? '#475569';
        const spec = VEHICLE_SPECS[v.type];
        // Phase 13.9 — Icon nach category, Polizei = Shield, Hubschrauber = Plane.
        const Icon = spec.airUnit
          ? Plane
          : spec.category === 'fire'
            ? Truck
            : spec.category === 'police'
              ? Shield
              : Cross;

        return (
          <Marker key={v.id} longitude={pos.lng} latitude={pos.lat} anchor="center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                selectDetail({ kind: 'vehicle', id: v.id });
              }}
              title={`${spec.label} · Status ${displayStatus}`}
              aria-label={`${spec.label} – Status ${displayStatus}`}
              className={[
                'relative flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-lg transition hover:scale-110',
                displayStatus === 5 ? 'animate-pulse' : '',
              ].join(' ')}
              style={{ border: `2.5px solid ${ring}` }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: ring }} aria-hidden />
              <span
                className="absolute -bottom-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold text-white shadow"
                style={{ background: ring }}
                aria-hidden
              >
                {displayStatus}
              </span>
            </button>
          </Marker>
        );
      })}
    </>
  );
}

function computeLivePosition(v: Vehicle, gameTime: number): { lat: number; lng: number } {
  // Stationary or no route → use stored position.
  if (
    v.routeCoords === undefined ||
    v.legStartedAt === undefined ||
    v.legDurationMs === undefined ||
    v.legDurationMs <= 0
  ) {
    return v.position;
  }
  const elapsed = gameTime - v.legStartedAt;
  const t = Math.max(0, Math.min(1, elapsed / v.legDurationMs));
  return pointAlongLine(v.routeCoords, t);
}
